"use client";

import { useState, useEffect, startTransition } from "react";
import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import Link from "next/link";

interface HeroProps {
  badge?: string;
  titlePrefix?: string;
  typingPhrases: string[];
  description: string;
  ctaText: string;
  ctaHref?: string;
  demoHref?: string;
}

export function Hero({
  badge,
  titlePrefix = "Monetize your",
  typingPhrases,
  description,
  ctaText,
  ctaHref = "/creator",
  demoHref = "#demo",
}: Readonly<HeroProps>) {
  const t = useTranslations("Hero");
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    const currentPhrase = typingPhrases[currentPhraseIndex];

    if (isTyping) {
      if (displayedText.length < currentPhrase.length) {
        const timeout = setTimeout(
          () => {
            setDisplayedText(currentPhrase.slice(0, displayedText.length + 1));
          },
          80 + Math.random() * 40,
        );
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setIsTyping(false);
        }, 2500);
        return () => clearTimeout(timeout);
      }
    } else if (displayedText.length > 0) {
      const timeout = setTimeout(() => {
        setDisplayedText(displayedText.slice(0, -1));
      }, 30);
      return () => clearTimeout(timeout);
    } else {
      startTransition(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % typingPhrases.length);
        setIsTyping(true);
      });
    }
  }, [displayedText, isTyping, currentPhraseIndex, typingPhrases]);

  return (
    <div className="bg-white dark:bg-slate-900">
      <div className="relative isolate px-6 pt-10 lg:px-8">
        <div className="mx-auto max-w-3xl py-16 sm:py-24">
          <div className="text-center">
            {/* Badge */}
            {badge && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mb-8"
              >
                <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  {badge}
                </span>
              </motion.div>
            )}

            {/* Main heading with typing animation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 className="text-5xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl dark:text-white">
                <span className="block">{titlePrefix}</span>
                <span className="block min-h-[1.1em] text-purple-600 dark:text-purple-400">
                  {displayedText}
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{
                      duration: 0.7,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                    className="text-purple-600"
                  >
                    |
                  </motion.span>
                </span>
              </h1>
            </motion.div>

            {/* Value proposition */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mx-auto mt-8 max-w-2xl text-lg leading-8 text-gray-600 sm:text-xl dark:text-gray-300"
            >
              {description}
              <span className="mt-3 block font-medium text-gray-900 dark:text-white">
                {t("badge")}
              </span>
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-10 flex items-center justify-center gap-x-6"
            >
              {/* Used prefetch={false} to avoid caching the link for sign-in */}
              <Link
                href={ctaHref}
                prefetch={false}
                className="rounded-lg bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-purple-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
              >
                {ctaText}
              </Link>

              <Link
                href={demoHref}
                className="text-sm leading-6 font-semibold text-gray-900 transition-colors hover:text-purple-600 dark:text-white dark:hover:text-purple-400"
              >
                {t("ctaSecondary")} <span aria-hidden="true">→</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
